import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { openComment } from './openComment';
import {
  CommentAnchorApi, CommentAnchorProvider, useDomCommentAnchors
} from './commentAnchors';
import CommentComposer, { ComposerSubmission } from './CommentComposer';
import type {
  User, ReviewCycle, ReviewComment, ParsedSection,
  ChildReview, ContentType, SectionType, WebPageState
} from './types';
import SnapshotViewer from './SnapshotViewer';
import ViewerSidePanel from './ViewerSidePanel';
import { isParsedContentWorkInFlight } from './parsedPolling';
import { hasEditableWorkingFile } from './workingFile';
import { mentionsViewer, markerMentionProps } from './mentions';
import './ContentReview.css';

/**
 * Is this URL an external player PAGE rather than a playable media file?
 *
 * Web-page cycles spawn a child cycle per embedded video, and for an iframe
 * embed the only thing we can capture is the player's own URL — there is no
 * binary to extract, unlike pptx embedded media which is unzipped out of the
 * file. Feeding that URL to <video src> renders a player with controls and no
 * picture, because the "video" is an HTML document. Found in prod 2026-08-19 on
 * the first web-page cycle: the HubSpot player URL sat in a dead <video> tag.
 *
 * Keep this list in step with the scraper's detection list
 * (`webPageScraperService.js`, the iframe branch) — a provider it detects but
 * this misses reproduces exactly the dead-player bug.
 */
const PLAYER_PAGE_HOSTS = ['youtube', 'vimeo', 'wistia', 'vidyard', 'loom', 'hubspotvideo'];

export function isProviderPlayerUrl(url?: string | null): boolean {
  if (!url) return false;
  try {
    const host = new URL(url).hostname;
    return PLAYER_PAGE_HOSTS.some(p => host.includes(p));
  } catch {
    // Not an absolute URL — a local media path, which <video> handles.
    return false;
  }
}

/**
 * Point a provider player at US as its embedding parent.
 *
 * 2026-08-19, measured: the HubSpot player posts its state to the ONE origin
 * named in the `parentOrigin` query param — not to `*`. We frame the URL
 * exactly as captured from the source page, so that param still names the
 * original site and every message we listen for is delivered to nobody:
 *
 *   parentOrigin = original site -> 0 time updates
 *   parentOrigin = our origin    -> 34 time updates
 *   parentOrigin = absent        -> 0 time updates
 *
 * Rewriting it is what an embedder is supposed to do. Only touched for hosts
 * that use this param, so a YouTube/Vimeo src is passed through untouched.
 */
export function playerSrcForEmbedding(url: string): string {
  try {
    const u = new URL(url);
    if (u.searchParams.has('parentOrigin') || u.hostname.includes('hubspotvideo')) {
      u.searchParams.set('parentOrigin', window.location.origin);
    }
    return u.toString();
  } catch {
    return url;
  }
}

interface DocumentViewerProps {
  user: User | null;
  cycle: ReviewCycle;
  comments: ReviewComment[];
  getAuthHeaders: () => Promise<Record<string, string>>;
  onCommentAdded: () => void;
  readOnly?: boolean;
  // 'consultation' for mentioned participants (D69): selection + pins still
  // work, but the composer only offers "Ask for input". Distinct from
  // readOnly, which removes commenting altogether.
  // 'reply-only' (2026-08-24) — the named producer. Every authoring
  // affordance goes (selection, pins, drags, timestamps, composer); reading
  // and replying are untouched, because replies are authored in the Comment
  // Summary rather than here.
  composerMode?: 'full' | 'consultation' | 'reply-only';
  /**
   * Changing this refetches parsed content and CHILD CYCLES.
   *
   * 2026-08-21 (Fredrik's prod walk): marking an embedded video reviewed left
   * the child tab still captioned "not reviewed". The write succeeded and the
   * host's own child state refreshed — but the tab caption reads this
   * component's `children`, fetched once on mount, and nothing re-asked.
   * Hosts pass a counter they bump whenever they refetch the cycle.
   *
   * REQUIRED on purpose. It shipped optional and two of the three call sites
   * simply never passed it, which silently reinstated the bug at those sites —
   * found by audit, not by anyone noticing. A required prop cannot be
   * forgotten.
   */
  refreshKey: string | number;
  /**
   * May this viewer edit SOMEONE ELSE'S comment? Author-edits are decided per
   * card from the signed-in address; this is the decision-maker override, and
   * it mirrors PUT /comments/:id (author OR content_review.decision_maker,
   * admin auto-granted).
   *
   * It used to be derived here as `!readOnly`, which was the rule INVERTED:
   * an assigned reviewer (readOnly=false) got an Edit button on every
   * colleague's finding and a 403 when they used it, while the decision maker
   * — the one person the backend does allow — saw none at all, because
   * DecisionView renders the viewer read-only (Fredrik, reviewing as Rita,
   * 2026-08-26). Passed from the dashboard's canMakeDecisions.
   */
  canEditAnyComment?: boolean;
  /**
   * What a comment row in the rail expands into. The list, its collapse and
   * the jump-to-content live in the rail; the CARD stays the host's, so
   * ReviewInterface keeps its reviewer card and DecisionView keeps the one
   * with the disposition control and its per-item confirmation gate. Omitted
   * (PreScreenView) means no comments tab at all.
   */
  renderCommentDetail?: (comment: ReviewComment) => React.ReactNode;
  /** Counters and bulk actions above the rail's comment list. */
  commentsHeader?: React.ReactNode;
  // Phase 6A: visibility filter for inline comment markers. When omitted, falls
  // back to legacy behaviour (reviewers see own only, decision makers see all).
  commentVisibility?: 'mine' | 'all' | 'others';
  // Phase 11.2: optional controlled activeTab (parent ↔ child cycle navigation).
  // When provided, the parent component owns the state — used by ReviewInterface
  // to render a per-cycle assessment / mark-reviewed panel beside the viewer.
  // When omitted, DocumentViewer uses its own internal state (PreScreenView /
  // DecisionView call sites don't need to track this).
  activeTab?: string;
  onActiveTabChange?: (tab: string) => void;
  // Whether the current user may delete a CHILD cycle from its tab. Caller
  // passes it in, exactly as CancelCycleButton does, so permission logic stays
  // in one place. Added 2026-08-19: until then a child cycle could not be
  // deleted through the UI at ALL — the dashboard filters out anything with a
  // parentReview, and the delete control lives only on dashboard rows. A
  // duplicate child was therefore permanently stuck, and duplicates are not
  // cosmetic: a reviewer cannot submit an assessment until every child is
  // marked reviewed (ReviewInterface), so a stray child blocks real work.
  canDeleteChild?: boolean;
  // Called after a child is deleted so the caller can refetch. The viewer does
  // not own the cycle list.
  onChildDeleted?: () => void;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const CONTENT_TYPE_ICONS: Record<string, string> = {
  docx: 'Word', pptx: 'PowerPoint', pdf: 'PDF',
  image: 'Image file', video: 'Video file', webpage: 'Web page'
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  user, cycle, comments, getAuthHeaders, onCommentAdded, readOnly = false, composerMode = 'full',
  refreshKey, commentVisibility,
  activeTab: controlledActiveTab, onActiveTabChange,
  canDeleteChild = false, onChildDeleted,
  canEditAnyComment = false, renderCommentDetail, commentsHeader
}) => {
  // Can this viewer author a NEW comment here? readOnly is the decision
  // maker's consolidated view; 'reply-only' is the producer, who reads
  // everything and answers in threads but never opens one.
  const canAuthor = !readOnly && composerMode !== 'reply-only';
  const [sections, setSections] = useState<ParsedSection[]>(cycle.parsedContent || []);
  const [children, setChildren] = useState<ChildReview[]>([]);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  // Web page multi-state support (tabs / carousel slides)
  const [webpageStates, setWebpageStates] = useState<WebPageState[]>(cycle.screenshots || []);
  const [activeStateId, setActiveStateId] = useState<string>('default');
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState<ParsedSection | null>(null);
  const [clickCoords, setClickCoords] = useState<{ x: number; y: number; width?: number; height?: number } | null>(null);
  // Layout view (2026-09-01, Fredrik) — per-slide/page renders of the
  // DESIGNED content, commentable like an image: text baked into images,
  // visual placement, everything the extracted copy cannot show. 'copy'
  // keeps today's text view; findings that should auto-apply still belong
  // there (text selection is the anchor the apply pipeline runs on).
  const [viewMode, setViewMode] = useState<'copy' | 'layout'>('copy');
  const [layoutState, setLayoutState] = useState<{ status: string; pages?: number; error?: string } | null>(null);
  const [layoutUrls, setLayoutUrls] = useState<string[]>([]);
  const [layoutSection, setLayoutSection] = useState<{ sectionId: string; sectionType: string } | null>(null);
  const [layoutPage, setLayoutPage] = useState<number | null>(null);
  const [layoutImgSizes, setLayoutImgSizes] = useState<Record<number, { w: number; h: number }>>({});
  const [layoutThumb, setLayoutThumb] = useState<string | null>(null);
  const [videoTimestamp, setVideoTimestamp] = useState<number | null>(null);
  /**
   * Playback position of an externally-hosted player, learned by listening.
   *
   * 2026-08-19: a provider player is a cross-origin iframe, so videoRef is
   * useless and we assumed timestamp comments were simply impossible there.
   * They are not — the HubSpot player BROADCASTS its position to the parent
   * window, verified against the live player on the staged page:
   *
   *   {"type":"PLAYER_TIME_UPDATE","payload":{"currentTime":0.919232},
   *    "videoId":290504114386,"videoTitle":"…"}
   *
   * so we get the same number a <video> element would have given us, without
   * reaching inside the frame and without the provider's SDK.
   */
  const [framedTime, setFramedTime] = useState<number | null>(null);
  /**
   * Playback position of a NATIVE <video>, for the button label.
   *
   * The embedded-player button has counted since 2026-08-19 ("Comment at
   * 0:12"), while the native one read "Comment at current time" — the same act
   * described two different ways, and the static wording makes a reviewer
   * check the player to find out what they are about to stamp (Fredrik,
   * 2026-08-24, from a live video cycle). The time was always available here;
   * only the label never showed it.
   *
   * Rounded to whole seconds ON WRITE, so `timeupdate` (which fires ~4×/s)
   * re-renders at most once a second instead of on every tick. The label
   * displays whole seconds anyway, so nothing is lost.
   */
  const [nativeTime, setNativeTime] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // A refused write used to go to console.error only, so the composer just sat
  // there — Fredrik hit exactly that as a producer, whose findings the server
  // refuses (2026-08-21). Say what happened.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoCleanupRef = useRef<(() => void) | null>(null);
  // Position to put the player back to after a remount, plus the src it was
  // measured against — see attachVideo. Null when there is nothing to restore.
  const videoResumeRef = useRef<{ src: string; time: number } | null>(null);
  const [parsing, setParsing] = useState(false);
  // Text selection state
  const [textSelection, setTextSelection] = useState<{ text: string; sectionId: string; start: number; end: number } | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Callback ref: the popover keeps using contentRef (it reads on demand),
  // while the anchor spy needs to be TOLD when the element attaches — see
  // useDomCommentAnchors. `.viewer-content` does not exist on the first
  // render of a file cycle, and a plain ref makes that invisible to effects.
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
  const attachContentRef = useCallback((el: HTMLDivElement | null) => {
    contentRef.current = el;
    setContentEl(el);
  }, []);
  // Rectangle drag state for screenshots
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragRect, setDragRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Hover popover for comments
  const [hoveredComments, setHoveredComments] = useState<{ comments: ReviewComment[]; x: number; y: number } | null>(null);
  // Track image load to ensure rectangles render with correct dimensions
  const [imageLoaded, setImageLoaded] = useState(false);
  // Toggle to hide inline comment markers (for decision makers)
  const [showInlineComments, setShowInlineComments] = useState(true);
  // Local comments state — syncs from props, also updated locally after inline submissions
  const [localComments, setLocalComments] = useState<ReviewComment[]>(comments);

  // Sync when parent passes updated comments
  useEffect(() => {
    setLocalComments(comments);
  }, [comments]);

  // Stable ref to getAuthHeaders so effects don't re-run every time the parent re-renders
  const getAuthHeadersRef = useRef(getAuthHeaders);
  useEffect(() => { getAuthHeadersRef.current = getAuthHeaders; }, [getAuthHeaders]);

  // Reset image loaded state when screenshot changes
  useEffect(() => {
    setImageLoaded(false);
  }, [screenshotUrl]);
  // Active tab: 'parent' or a child review ID. Controlled if a parent component
  // (ReviewInterface) provides activeTab + onActiveTabChange; otherwise we keep
  // local state (used by PreScreenView / DecisionView).
  const [internalActiveTab, setInternalActiveTab] = useState<string>('parent');
  const activeTab = controlledActiveTab ?? internalActiveTab;
  const setActiveTab = (tab: string) => {
    if (onActiveTabChange) onActiveTabChange(tab);
    else setInternalActiveTab(tab);
  };
  // Cache child data when loaded
  const [childData, setChildData] = useState<Record<string, { cycle: ReviewCycle; comments: ReviewComment[]; mediaUrl?: string }>>({});

  // The per-child cache is write-once (its loader early-returns when the entry
  // exists), so refreshing the child LIST while leaving this populated would
  // still show first-visit data — e.g. a child comment resolved from the parent
  // tab still rendering unresolved. Clearing it on refresh makes refreshKey
  // mean what it says (2026-08-21 audit).
  useEffect(() => {
    setChildData({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);
  const [loadingChild, setLoadingChild] = useState(false);

  // Fetch parsed content and children — poll while the backend is still
  // producing them (scrape, snapshot capture, or on-demand file parse).
  //
  // 2026-08-21 (Fredrik's prod E2E, cycle 6a87fcc5): the video child tab did
  // not appear until a manual refresh. Two reasons, both here:
  //
  //   1. The stop condition was `!hasSections && !hasScreenshot`. A
  //      snapshot-mode webpage never populates either — that IS its design —
  //      so the poll could only ever end by running out of attempts, and
  //      whether you saw the child was luck.
  //   2. The ceiling was 30 polls x 2 s = 60 s, while the same file's comment
  //      estimates a cold capture at ~70 s. Measured: cycle created 07:22:45,
  //      child created 07:24:16 — 31 s AFTER the poll gave up.
  //
  // Now it keys off the capture's own status (`snapshotStatus`, added to the
  // parsed-content payload the same day) and uses a wall-clock deadline. The
  // extra "settle" poll matters: the backend saves snapshotStatus='ready'
  // BEFORE creating the media children, so the response that first reports
  // ready can legitimately carry zero children.
  useEffect(() => {
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    let everInFlight = false;
    let settlePollDone = false;
    const POLL_INTERVAL_MS = 2000;
    // Webpages own the slow path (snapshot capture); files parse faster but a
    // large deck on a cold SharePoint fetch is not instant either.
    const POLL_DEADLINE_MS = cycle.contentType === 'webpage' ? 5 * 60 * 1000 : 2 * 60 * 1000;
    const startedAt = Date.now();

    const fetchParsed = async (isInitial: boolean) => {
      try {
        if (isInitial) setLoading(true);
        const headers = await getAuthHeadersRef.current();
        const res = await axios.get(`${backendUrl}/api/content-review/cycles/${cycle._id}/parsed-content`, { headers });
        if (cancelled) return;
        const hasSections = res.data.sections?.length > 0;
        const hasScreenshot = !!res.data.screenshotPath;
        const stateArray: WebPageState[] = res.data.screenshots || [];

        if (hasSections) {
          setSections(res.data.sections);
        }
        if (stateArray.length > 0) {
          setWebpageStates(stateArray);
        }
        setChildren(res.data.children || []);

        // Is the backend still producing content for this cycle?
        const workInFlight = isParsedContentWorkInFlight({
          contentType: cycle.contentType,
          hasFileId: !!cycle.contentItem?.fileId,
          snapshotStatus: res.data.snapshotStatus ?? cycle.snapshotStatus ?? null,
          hasSections,
          hasScreenshot,
          parsingInFlight: res.data.parsingInFlight,
          scrapingInFlight: res.data.scrapingInFlight
        });
        if (workInFlight) everInFlight = true;

        const withinDeadline = Date.now() - startedAt < POLL_DEADLINE_MS;
        if (!cancelled && withinDeadline) {
          if (workInFlight) {
            pollTimer = setTimeout(() => fetchParsed(false), POLL_INTERVAL_MS);
          } else if (everInFlight && !settlePollDone) {
            // One more pass to pick up children written just after the status
            // flipped to ready.
            settlePollDone = true;
            pollTimer = setTimeout(() => fetchParsed(false), POLL_INTERVAL_MS);
          }
        }

        // If screenshot arrived during polling, update it (only the default screenshot —
        // the main loadScreenshot effect handles state-specific screenshots)
        if (hasScreenshot && !screenshotUrl) {
          try {
            const ssRes = await axios.get(
              `${backendUrl}/api/content-review/cycles/${cycle._id}/screenshot`,
              { headers, responseType: 'blob' }
            );
            if (!cancelled) setScreenshotUrl(URL.createObjectURL(ssRes.data));
          } catch {}
        }
      } catch (err) {
        console.error('Error fetching parsed content:', err);
      } finally {
        if (isInitial && !cancelled) setLoading(false);
      }
    };
    fetchParsed(true);

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
    // NOTE: getAuthHeaders intentionally excluded — accessed via ref — so this
    // effect doesn't re-run every time the parent re-renders (e.g. after a
    // comment submit triggers a parent refetch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle._id, cycle.contentType, refreshKey]);

  // Load screenshot for webpage type — refetches when activeStateId changes
  useEffect(() => {
    if (cycle.contentType !== 'webpage') return;
    if (!cycle.screenshotPath && webpageStates.length === 0) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    const loadScreenshot = async () => {
      try {
        const headers = await getAuthHeadersRef.current();
        const url = activeStateId && activeStateId !== 'default'
          ? `${backendUrl}/api/content-review/cycles/${cycle._id}/screenshot?stateId=${encodeURIComponent(activeStateId)}`
          : `${backendUrl}/api/content-review/cycles/${cycle._id}/screenshot`;
        const res = await axios.get(url, { headers, responseType: 'blob' });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setScreenshotUrl(objectUrl);
      } catch (err) {
        console.error('Error loading screenshot:', err);
      }
    };
    loadScreenshot();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // getAuthHeaders accessed via ref to avoid re-running on parent re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle._id, cycle.contentType, cycle.screenshotPath, webpageStates.length, activeStateId]);

  // Load media URL for video/image content types
  useEffect(() => {
    if ((cycle.contentType === 'video' || cycle.contentType === 'image') && cycle.contentItem.fileId) {
      const loadMedia = async () => {
        try {
          const headers = await getAuthHeadersRef.current();
          const res = await axios.get(`${backendUrl}/api/content-review/cycles/${cycle._id}/download-url`, { headers });
          setMediaUrl(res.data.downloadUrl);
        } catch (err) {
          console.error('Error loading media URL:', err);
        }
      };
      loadMedia();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle._id, cycle.contentType, cycle.contentItem.fileId]);

  // Load child data when a child tab is selected
  useEffect(() => {
    if (activeTab === 'parent' || childData[activeTab]) return;
    const loadChild = async () => {
      try {
        setLoadingChild(true);
        const headers = await getAuthHeadersRef.current();
        const [cycleRes, urlRes] = await Promise.all([
          axios.get(`${backendUrl}/api/content-review/cycles/${activeTab}`, { headers }),
          axios.get(`${backendUrl}/api/content-review/cycles/${activeTab}/download-url`, { headers }).catch(() => ({ data: {} }))
        ]);
        setChildData(prev => ({
          ...prev,
          [activeTab]: {
            cycle: cycleRes.data.cycle,
            comments: cycleRes.data.comments,
            mediaUrl: urlRes.data.downloadUrl
          }
        }));
      } catch (err) {
        console.error('Error loading child:', err);
      } finally {
        setLoadingChild(false);
      }
    };
    loadChild();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, childData]);

  // Determine what's being viewed right now
  const [deletingChild, setDeletingChild] = useState(false);

  /**
   * Opens the comment form with no anchor.
   *
   * 2026-08-19: every other content type reaches the comment form through an
   * anchor — a text selection, a section/pin click, or a video timestamp. An
   * externally-hosted video has none of those: it is a cross-origin iframe, so
   * there is no currentTime to stamp and no DOM to select. When the timestamp
   * button was removed for framed players earlier today (a1bd96c2), the note
   * that replaced it pointed at "the review comments below" — which do not
   * exist. The result was a video that could not be commented on at all, found
   * by Fredrik in the E2E run the same afternoon.
   */
  const [manualComment, setManualComment] = useState(false);

  /**
   * Delete a child cycle from its own tab.
   *
   * Uses the SAME `DELETE /cycles/:id` endpoint as the dashboard, so the
   * backend's deleteBlockReason guards still apply — an approved child, or one
   * carrying automation, is refused here exactly as it would be there. This is
   * a new place to reach an existing destructive action, not a new one.
   *
   * Deliberately NOT "detach then delete": detaching leaves the child on the
   * main dashboard as an orphan with no file, channel or audience, which fails
   * the D81 source gate and can never progress — visible to the whole team and
   * looking like real work. One step avoids that intermediate state.
   */
  const handleDeleteChild = useCallback(async (childId: string, childName: string) => {
    if (!window.confirm(`Delete "${childName}"?\n\nThis removes the child review entirely. It cannot be undone.`)) return;
    try {
      setDeletingChild(true);
      const headers = await getAuthHeadersRef.current();
      await axios.delete(`${backendUrl}/api/content-review/cycles/${childId}`, { headers });
      setActiveTab('parent');
      if (onChildDeleted) onChildDeleted();
    } catch (err: any) {
      // The backend explains WHY it refused (deleteBlockReason). Surfacing that
      // verbatim beats a generic failure — "a final decision is recorded
      // against it" is actionable; "could not delete" is not.
      const reason = err?.response?.data?.error || err?.message || 'Unknown error';
      window.alert(`Could not delete this item: ${reason}`);
    } finally {
      setDeletingChild(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChildDeleted]);

  const isViewingChild = activeTab !== 'parent';
  const activeChild = isViewingChild ? childData[activeTab] : null;
  const activeCycle = isViewingChild && activeChild ? activeChild.cycle : cycle;
  const allActiveCommentsRaw = isViewingChild && activeChild ? activeChild.comments : localComments;

  // For webpages with multiple states: only show comments made on the currently-active state
  const isWebpage = (activeCycle?.contentType || cycle.contentType) === 'webpage';
  const hasMultipleStates = !isViewingChild && webpageStates.length > 1;
  const allActiveComments = isWebpage && hasMultipleStates
    ? allActiveCommentsRaw.filter(c => (c.stateId || 'default') === activeStateId)
    : allActiveCommentsRaw;

  // For inline display: filter by Phase 6A visibility prop when provided,
  // otherwise fall back to legacy behaviour (decision maker sees all, reviewer sees own only).
  const applyVisibility = (list: ReviewComment[]) => {
    if (commentVisibility === 'all') return list;
    if (commentVisibility === 'others') return list.filter(c => c.reviewer.email !== user?.email);
    if (commentVisibility === 'mine') return list.filter(c => c.reviewer.email === user?.email);
    // Legacy fallback when caller didn't pass the prop
    return readOnly ? list : list.filter(c => c.reviewer.email === user?.email);
  };
  const visibleInlineComments = !showInlineComments ? [] : applyVisibility(allActiveComments);
  const railComments = applyVisibility(allActiveComments)
    .filter(c => !c.parentComment && (c.kind || 'finding') !== 'consultation');

  // ── Comment anchors (2026-08-26) ──────────────────────────────────────
  //
  // The rail asks two questions of whatever is on screen — "put this comment's
  // anchor in view" and "which anchors are in view" — and this is where the
  // file formats answer them. Text, screenshot and image anchors are real
  // elements, so the DOM adapter covers all three at once. Video is the one
  // that cannot be: its anchor is a moment, not a place.
  const anchorSignature = [
    activeTab, activeStateId, showInlineComments ? '1' : '0',
    visibleInlineComments.map(c => c._id).join(',')
  ].join('|');
  const domAnchors = useDomCommentAnchors(contentEl, anchorSignature);

  const isVideoContent = (activeCycle?.contentType || cycle.contentType) === 'video';
  // The embedded-player path LEARNS the position by listening (framedTime) but
  // has no way to set it, so those cycles get a spy and no jump — reported
  // honestly through hasAnchor rather than a button that does nothing.
  const playbackTime = framedTime !== null ? framedTime : nativeTime;
  const commentById = useMemo(() => {
    const m = new Map<string, ReviewComment>();
    for (const c of allActiveComments) m.set(c._id, c);
    return m;
  }, [allActiveComments]);

  const videoVisibleIds = useMemo(() => {
    if (!isVideoContent) return [];
    return allActiveComments
      .filter(c => typeof c.timestamp === 'number' && Math.abs(c.timestamp - playbackTime) <= 2)
      .map(c => c._id);
  }, [isVideoContent, allActiveComments, playbackTime]);

  // A video's "document order" is the timeline. Unlike the visible set this
  // does not depend on the playhead, so it is memoised apart from it — the
  // list must not reorder itself while the video plays.
  const videoOrderedIds = useMemo(() => {
    if (!isVideoContent) return [];
    return allActiveComments
      .filter(c => typeof c.timestamp === 'number')
      .sort((a, b) => (a.timestamp as number) - (b.timestamp as number))
      .map(c => c._id);
  }, [isVideoContent, allActiveComments]);

  const anchors = useMemo<CommentAnchorApi>(() => {
    if (!isVideoContent) return domAnchors;
    const timestampOf = (id: string) => {
      const t = commentById.get(id)?.timestamp;
      return typeof t === 'number' ? t : null;
    };
    return {
      reveal: (id: string) => {
        const ts = timestampOf(id);
        if (ts === null || !videoRef.current) return false;
        videoRef.current.currentTime = ts;
        return true;
      },
      visibleCommentIds: videoVisibleIds,
      orderedCommentIds: videoOrderedIds,
      // A video's anchor is a moment, not an element — there is nothing in
      // the DOM to mark, and the playhead already shows where you are. Same
      // for hovering and for asking where the anchor is on screen: the
      // honest answer is "nowhere", and saying so lets the rail fall back
      // instead of aligning itself to a rectangle that does not exist.
      setActive: () => {},
      setHovered: () => {},
      hoveredCommentId: null,
      anchorRect: () => null,
      hasAnchor: (id: string) => timestampOf(id) !== null && !!videoRef.current
    };
  }, [isVideoContent, domAnchors, commentById, videoVisibleIds, videoOrderedIds]);
  const activeMediaUrl = isViewingChild && activeChild ? activeChild.mediaUrl || null : mediaUrl;

  // For webpages: use the active state's sections (tab / carousel slide)
  // For other content: use the top-level parsed sections
  const activeWebpageState = isWebpage && !isViewingChild
    ? webpageStates.find(s => s.stateId === activeStateId) || webpageStates[0]
    : null;
  // Memoised so useCallback deps below stay stable across renders
  const activeSections = useMemo(() => {
    if (isViewingChild) return activeChild?.cycle.parsedContent || [];
    if (activeWebpageState) return activeWebpageState.sections;
    return sections;
  }, [isViewingChild, activeChild, activeWebpageState, sections]);

  // Handle text selection on mouseup — directly opens comment form
  const handleContentMouseUp = useCallback((e: React.MouseEvent) => {
    if (readOnly || composerMode === 'reply-only') return;

    // Don't interfere if clicking inside the comment form
    const target = e.target as HTMLElement;
    if (target.closest('.viewer-comment-form')) return;

    // Small delay so the browser finalizes the selection
    setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !selection.toString().trim()) {
        return;
      }

      const selectedText = selection.toString().trim();
      if (selectedText.length < 2) return;

      // Find which section the selection is in
      const anchorNode = selection.anchorNode;
      const sectionEl = anchorNode?.parentElement?.closest?.('[data-section-id]') as HTMLElement | null;
      if (!sectionEl || !contentRef.current?.contains(sectionEl)) {
        return;
      }

      const sectionId = sectionEl.getAttribute('data-section-id') || '';
      const section = activeSections.find(s => s.sectionId === sectionId);
      if (!section) return;

      // Compute selection offsets within the section content.
      // Measure WHERE the user selected (DOM offset from the section start)
      // rather than searching for WHAT they selected — a string search
      // always lands on the first occurrence, so the second "(ED)" on a
      // slide used to anchor to the first. The rendered section is plain
      // text plus inline <mark>s, so the DOM text length equals the offset
      // into section.content; the check below verifies that assumption
      // before trusting it.
      const sectionText = section.content || '';
      let start = -1;
      let end = -1;
      try {
        const range = selection.getRangeAt(0);
        const before = document.createRange();
        before.selectNodeContents(sectionEl);
        before.setEnd(range.startContainer, range.startOffset);
        const raw = selection.toString();
        const leadingWs = raw.length - raw.trimStart().length;
        const domStart = before.toString().length + leadingWs;
        if (sectionText.substr(domStart, selectedText.length) === selectedText) {
          start = domStart;
          end = domStart + selectedText.length;
        }
      } catch {
        // fall through to the string search
      }

      // Fallback: string search (first occurrence)
      if (start < 0) {
        start = sectionText.indexOf(selectedText);
        end = start >= 0 ? start + selectedText.length : -1;
      }

      // Fallback: try normalized whitespace match
      if (start < 0) {
        const normalizedSection = sectionText.replace(/\s+/g, ' ');
        const normalizedSelection = selectedText.replace(/\s+/g, ' ');
        const normStart = normalizedSection.indexOf(normalizedSelection);
        if (normStart >= 0) {
          start = normStart;
          end = normStart + normalizedSelection.length;
        } else {
          // Last resort: mark the whole section
          start = 0;
          end = sectionText.length;
        }
      }

      setTextSelection({
        text: selectedText,
        sectionId,
        start,
        end
      });
      setSelectedSection(section);
      setClickCoords(null);
      setVideoTimestamp(null);
    }, 10);
  }, [readOnly, composerMode, activeSections]);

  // Rectangle drag on screenshots: mousedown
  const handleScreenshotMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!canAuthor) return;
    const img = imageRef.current;
    if (!img) return;
    e.preventDefault();

    const rect = img.getBoundingClientRect();
    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    const x = Math.round((e.clientX - rect.left) * scaleX);
    const y = Math.round((e.clientY - rect.top) * scaleY);

    setDragStart({ x, y });
    setDragRect(null);
    setIsDragging(true);
  };

  // Rectangle drag: mousemove
  const handleScreenshotMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStart || !imageRef.current) return;
    const img = imageRef.current;
    const rect = img.getBoundingClientRect();
    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    const currentX = Math.round((e.clientX - rect.left) * scaleX);
    const currentY = Math.round((e.clientY - rect.top) * scaleY);

    setDragRect({
      x: Math.min(dragStart.x, currentX),
      y: Math.min(dragStart.y, currentY),
      width: Math.abs(currentX - dragStart.x),
      height: Math.abs(currentY - dragStart.y)
    });
  }, [isDragging, dragStart]);

  // Rectangle drag: mouseup
  // Uses `activeSections` so the collected context comes from the currently-
  // active webpage state (tab / carousel slide), not from the original
  // default-view sections.
  const handleScreenshotMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    if (dragRect && dragRect.width > 10 && dragRect.height > 10) {
      // Valid rectangle — collect ALL sections overlapping the rectangle
      const overlapping = activeSections.filter(s => {
        const b = s.metadata?.bounds;
        if (!b) return false;
        // Rectangle overlap check
        return dragRect.x < b.x + b.width && dragRect.x + dragRect.width > b.x &&
               dragRect.y < b.y + b.height && dragRect.y + dragRect.height > b.y;
      });

      // Combine content from all overlapping sections as context
      const combinedContent = overlapping.map(s => s.content).filter(Boolean).join('\n\n');
      const contextSection: ParsedSection | null = overlapping.length > 0
        ? { sectionId: overlapping[0].sectionId, type: 'dom_section', content: combinedContent, metadata: overlapping[0].metadata }
        : null;

      setClickCoords(dragRect);
      setSelectedSection(contextSection);
      setTextSelection(null);
      setVideoTimestamp(null);
    } else if (dragStart) {
      // Too small — treat as a pin click
      const nearestSection = activeSections.find(s => {
        const bounds = s.metadata?.bounds;
        if (!bounds) return false;
        return dragStart.x >= bounds.x && dragStart.x <= bounds.x + bounds.width &&
               dragStart.y >= bounds.y && dragStart.y <= bounds.y + bounds.height;
      }) || null;

      setClickCoords({ x: dragStart.x, y: dragStart.y });
      setSelectedSection(nearestSection);
      setTextSelection(null);
      setVideoTimestamp(null);
    }
    setDragStart(null);
    setDragRect(null);
  }, [isDragging, dragRect, dragStart, activeSections]);

  // Handle image click (pin-based, for standalone images)
  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!canAuthor) return;
    const img = imageRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    const x = Math.round((e.clientX - rect.left) * scaleX);
    const y = Math.round((e.clientY - rect.top) * scaleY);

    setClickCoords({ x, y });
    setSelectedSection(null);
    setTextSelection(null);
    setVideoTimestamp(null);
  };

  // ── Layout view plumbing ────────────────────────────────────────────────
  const layoutCapable = !isViewingChild && ['pptx', 'pdf', 'docx'].includes(cycle.contentType || '');

  const layoutSectionIdFor = (pageIndex: number): string => {
    const ct = cycle.contentType;
    if (ct === 'pptx') return `pptx-slide-${pageIndex}`;   // rendition page == slide
    if (ct === 'pdf') return `pdf-page-${pageIndex}`;
    // docx pages don't map to parsed sections (mammoth blocks) — layout
    // comments carry their own page anchor and route to the comment sheet.
    return `docx-page-${pageIndex}`;
  };

  // Drag a rectangle to mark an area (an image, a diagram), or click for a
  // point pin. No parsed section is attached — the pin belongs to the slide
  // itself (sectionId via layoutSection); dumping the whole slide's copy as
  // context was noise (Fredrik, 2026-09-01). Shares drag state with the
  // webpage screenshot path — only one of the two views renders per cycle.
  const layoutDragImgRef = useRef<HTMLImageElement | null>(null);

  const layoutScaledPoint = (img: HTMLImageElement, e: React.MouseEvent) => {
    const rect = img.getBoundingClientRect();
    return {
      x: Math.round((e.clientX - rect.left) * (img.naturalWidth / rect.width)),
      y: Math.round((e.clientY - rect.top) * (img.naturalHeight / rect.height))
    };
  };

  const handleLayoutMouseDown = (pageIndex: number, e: React.MouseEvent<HTMLImageElement>) => {
    if (!canAuthor) return;
    e.preventDefault();
    const img = e.currentTarget;
    layoutDragImgRef.current = img;
    setDragStart(layoutScaledPoint(img, e));
    setDragRect(null);
    setIsDragging(true);
    setLayoutPage(pageIndex);
  };

  const handleLayoutMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !dragStart || !layoutDragImgRef.current) return;
    const { x, y } = layoutScaledPoint(layoutDragImgRef.current, e);
    setDragRect({
      x: Math.min(dragStart.x, x),
      y: Math.min(dragStart.y, y),
      width: Math.abs(x - dragStart.x),
      height: Math.abs(y - dragStart.y)
    });
  };

  // The comment's readable context: a JPEG crop of the marked region, taken
  // from the already-loaded page raster (an authed blob URL, so the canvas
  // is not tainted). A point pin crops a fixed window around the point.
  const cropLayoutThumb = (img: HTMLImageElement, a: { x: number; y: number; width?: number; height?: number }): string | null => {
    try {
      const pad = 12;
      let { x, y } = a;
      let w = a.width || 0, h = a.height || 0;
      if (!w || !h) { w = 480; h = 320; x -= w / 2; y -= h / 2; }
      x -= pad; y -= pad; w += pad * 2; h += pad * 2;
      x = Math.max(0, Math.min(x, img.naturalWidth - 1));
      y = Math.max(0, Math.min(y, img.naturalHeight - 1));
      w = Math.min(w, img.naturalWidth - x);
      h = Math.min(h, img.naturalHeight - y);
      if (w < 8 || h < 8) return null;
      const scale = Math.min(1, 360 / w);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, x, y, w, h, 0, 0, canvas.width, canvas.height);
      const url = canvas.toDataURL('image/jpeg', 0.75);
      return url.length <= 300000 ? url : null;
    } catch { return null; }
  };

  const handleLayoutMouseUp = (pageIndex: number) => {
    if (!isDragging) return;
    setIsDragging(false);
    const anchor = (dragRect && dragRect.width > 10 && dragRect.height > 10)
      ? dragRect
      : dragStart ? { x: dragStart.x, y: dragStart.y } : null;
    setDragStart(null);
    setDragRect(null);
    if (!anchor) return;
    setClickCoords(anchor);
    setSelectedSection(null);
    setLayoutSection({ sectionId: layoutSectionIdFor(pageIndex), sectionType: cycle.contentType === 'pptx' ? 'slide' : 'page' });
    setLayoutPage(pageIndex);
    setLayoutThumb(layoutDragImgRef.current ? cropLayoutThumb(layoutDragImgRef.current, anchor) : null);
    setTextSelection(null);
    setVideoTimestamp(null);
  };

  // A cancelled/cleared pin also clears its layout anchor.
  useEffect(() => {
    if (!clickCoords) { setLayoutSection(null); setLayoutPage(null); setLayoutThumb(null); }
  }, [clickCoords]);

  // Revealing a comment from the rail switches to the view that holds its
  // anchor (Fredrik, 2026-09-01): a coordinate comment lives on a Layout
  // page, a text one in Copy — in the wrong view the DOM adapter finds no
  // element and the click silently did nothing. The switch renders the
  // anchor asynchronously (Layout pages may still be fetching), so the
  // reveal retries until the element exists.
  const baseAnchorsRef = useRef(anchors);
  baseAnchorsRef.current = anchors;
  const [pendingReveal, setPendingReveal] = useState<string | null>(null);

  const anchorsWithViewSwitch = useMemo<CommentAnchorApi>(() => {
    if (!layoutCapable) return anchors;
    return {
      ...anchors,
      hasAnchor: (id: string) => {
        const c = commentById.get(id);
        return anchors.hasAnchor(id) || !!c?.coordinates || !!c?.textSelection;
      },
      reveal: (id: string) => {
        const c = commentById.get(id);
        const needs: 'copy' | 'layout' = c?.coordinates ? 'layout' : 'copy';
        if (viewMode !== needs) {
          setViewMode(needs);
          setPendingReveal(id);
          return true;
        }
        return anchors.reveal(id);
      }
    };
  }, [anchors, layoutCapable, viewMode, commentById]);

  useEffect(() => {
    if (!pendingReveal) return;
    let cancelled = false;
    let tries = 0;
    const attempt = () => {
      if (cancelled) return;
      if (baseAnchorsRef.current.reveal(pendingReveal)) { setPendingReveal(null); return; }
      if (++tries < 40) setTimeout(attempt, 250);
      else setPendingReveal(null);
    };
    setTimeout(attempt, 50);
    return () => { cancelled = true; };
  }, [pendingReveal]);

  // Poll layout status while in layout mode (async generation, snapshot-style).
  useEffect(() => {
    if (viewMode !== 'layout' || !layoutCapable) return;
    let cancelled = false;
    const load = async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${backendUrl}/api/content-review/cycles/${cycle._id}/layout`, { headers });
        if (cancelled) return;
        setLayoutState(res.data);
        if (res.data?.status === 'generating') setTimeout(load, 2500);
      } catch (err: any) {
        if (!cancelled) setLayoutState({ status: 'failed', error: err.response?.data?.error || 'Failed to load layout' });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [viewMode, layoutCapable, cycle._id, getAuthHeaders]);

  // Fetch page images as auth'd blobs once ready (img src can't carry headers).
  useEffect(() => {
    if (viewMode !== 'layout' || layoutState?.status !== 'ready' || !layoutState.pages) return;
    let cancelled = false;
    const urls: string[] = [];
    (async () => {
      try {
        const headers = await getAuthHeaders();
        // One failed page must not strand every page after it (a dev-server
        // restart mid-load left a 54-page deck stuck at 37, 2026-09-07):
        // retry that page once, then move on and leave its card loading.
        for (let n = 1; n <= (layoutState.pages || 0); n++) {
          for (let attempt = 0; attempt < 2; attempt++) {
            try {
              const res = await axios.get(
                `${backendUrl}/api/content-review/cycles/${cycle._id}/layout/pages/${n}`,
                { headers, responseType: 'blob' }
              );
              if (cancelled) return;
              urls[n - 1] = URL.createObjectURL(res.data);
              setLayoutUrls([...urls]);
              break;
            } catch {
              if (cancelled) return;
              if (attempt === 0) await new Promise(r => setTimeout(r, 1500));
            }
          }
        }
      } catch { /* headers unavailable — every card stays "Loading page…" */ }
    })();
    return () => { cancelled = true; urls.forEach(u => { if (u) URL.revokeObjectURL(u); }); };
  }, [viewMode, layoutState?.status, layoutState?.pages, cycle._id, getAuthHeaders]);

  /**
   * Listen for playback position broadcast by an externally-hosted player.
   *
   * Filtering is on the message ORIGIN, never the embedId: HubSpot mints a
   * fresh embedId uuid on every page load, so an id-keyed filter would match
   * on the first render and silently miss on the next. That volatile-
   * identifier trap has now bitten this feature twice in one day — once in
   * the duplicate-child dedupe, once in the snapshot placeholder matcher.
   *
   * Only the HubSpot shape is VERIFIED (captured live from the staged page).
   * YouTube, Vimeo and Wistia each use a different envelope; they will simply
   * not match here and fall back to an untimestamped comment, which is the
   * previous behaviour rather than a regression.
   */
  const framedPlayerSrc = (() => {
    const src = (activeMediaUrl || activeCycle?.sourceUrl || cycle.sourceUrl) as string | undefined;
    if (activeMediaUrl || !src || !isProviderPlayerUrl(src)) return null;
    return src;
  })();

  useEffect(() => {
    if (!framedPlayerSrc) { setFramedTime(null); return; }
    let playerOrigin: string;
    try { playerOrigin = new URL(framedPlayerSrc).origin; } catch { return; }

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== playerOrigin) return;
      let data: any = e.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { return; }
      }
      const t = data?.payload?.currentTime;
      if (typeof t === 'number' && isFinite(t) && t >= 0) setFramedTime(t);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [framedPlayerSrc]);

  // Keep the native player's position in state so the comment button can show
  // it.
  //
  // A CALLBACK REF rather than an effect: `videoSrc` and `isFramed` are
  // computed inside the render branch, not in component scope, so an effect
  // has nothing valid to depend on and would attach before the element exists.
  // This fires exactly when the <video> mounts and unmounts, which is the
  // moment that actually matters.
  //
  // `seeked` and `loadedmetadata` are listened to alongside `timeupdate`
  // because scrubbing without playing is the normal way of finding the frame
  // you want to comment on — the label has to follow that too, not just
  // playback.
  //
  // 2026-08-27 (Legal reviewer, live video cycle): "after submitting a comment
  // the video starts from 0 again". It is not the submit handler — that resets
  // only anchor state, and its comment saying the video stays put was written
  // about those resets. It is the REFRESH. `onCommentAdded()` bumps the
  // parent's refreshKey, the parsed-content effect re-runs with
  // `fetchParsed(true)`, that sets `loading` true, and DocumentViewer's
  // `if (loading)` early-return replaces the whole subtree with "Loading
  // document content...". The <video> is unmounted and a fresh element comes
  // back at 0:00 — so a reviewer who commented at 4:12 had to scrub back
  // there before writing the next one.
  //
  // Restore rather than prevent the remount: the blanking also costs the docx
  // scroll position (which is why submit carries three restoreScroll calls),
  // and not blanking on refresh is a change to the load path every format
  // shares. Flagged to Fredrik as the root cause; this fixes what he reported.
  //
  // Position is captured in the CLEANUP, not from `nativeTime`: the new
  // element fires `sync` at 0 as it mounts, so any value read afterwards is
  // already the reset one. It is also the exact second rather than the
  // rounded display value.
  //
  // Keyed on src so switching child tabs — a different video — does not
  // inherit the previous one's position. Seeking waits for `loadedmetadata`
  // because assigning currentTime before the timeline exists is a no-op.
  // Playback is deliberately NOT resumed: he pauses to comment, and a video
  // that starts playing when the form closes is its own bug.
  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    if (videoCleanupRef.current) { videoCleanupRef.current(); videoCleanupRef.current = null; }
    videoRef.current = el;
    if (!el) return;
    const sync = () => {
      const t = Math.round(el.currentTime);
      // Guarded so a ~4x/s timeupdate only re-renders when the displayed
      // second actually changes.
      setNativeTime(prev => (prev === t ? prev : t));
    };

    // The src this element was MOUNTED with. Read here rather than in the
    // cleanup because React updates the attribute in place on the commit
    // before the one that unmounts — so a teardown-time read can label the
    // outgoing video's position with the incoming video's src, and the guard
    // below would then wave a resume through onto a different clip.
    const attachedSrc = el.getAttribute('src');

    const resume = videoResumeRef.current;
    videoResumeRef.current = null;
    if (resume && resume.time > 0) {
      const seek = () => {
        if (attachedSrc !== resume.src) return;
        try { el.currentTime = resume.time; } catch {}
        sync();
      };
      if (el.readyState >= 1) seek();
      else el.addEventListener('loadedmetadata', seek, { once: true });
    }

    sync();
    el.addEventListener('timeupdate', sync);
    el.addEventListener('seeked', sync);
    el.addEventListener('loadedmetadata', sync);
    videoCleanupRef.current = () => {
      const t = el.currentTime;
      videoResumeRef.current = attachedSrc && isFinite(t) && t > 0
        ? { src: attachedSrc, time: t }
        : null;
      el.removeEventListener('timeupdate', sync);
      el.removeEventListener('seeked', sync);
      el.removeEventListener('loadedmetadata', sync);
    };
  }, []);

  // Handle video timestamp comment
  const handleVideoComment = () => {
    if (!canAuthor || !videoRef.current) return;
    const time = Math.round(videoRef.current.currentTime);
    setVideoTimestamp(time);
    setSelectedSection(null);
    setTextSelection(null);
    setClickCoords(null);
  };

  // Submit comment
  const handleSubmitComment = async (sub: ComposerSubmission) => {
    if (!sub.issue.trim()) return;

    // Preserve viewport scroll position across submit + refresh flow
    const savedScrollY = window.scrollY;
    const restoreScroll = () => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: savedScrollY, left: 0, behavior: 'auto' });
      });
    };

    try {
      setSubmitting(true);
      setSubmitError(null);
      const headers = await getAuthHeaders();

      // kind chosen in CommentComposer (spec §8). Consultations carry no
      // severity/suggestedFix — the server would force them anyway; not
      // sending them keeps the request honest about what it is.
      const body: Record<string, any> = {
        kind: sub.kind,
        issue: sub.issue.trim(),
        ...(sub.kind === 'finding'
          ? { severity: sub.severity, suggestedFix: (sub.suggestedFix || '').trim(), ...(sub.proposedText ? { proposedText: sub.proposedText } : {}) }
          : {})
      };

      if (selectedSection) {
        body.sectionId = selectedSection.sectionId;
        body.sectionContent = selectedSection.content.substring(0, 500);
        body.sectionType = getSectionType(activeCycle?.contentType || cycle.contentType, selectedSection);
      }

      if (textSelection) {
        body.textSelection = textSelection.text;
        body.selectionStart = textSelection.start;
        body.selectionEnd = textSelection.end;
      }

      if (clickCoords) {
        body.coordinates = clickCoords;
        const ct = activeCycle?.contentType || cycle.contentType;
        if (!body.sectionType) body.sectionType = ct === 'webpage' ? 'dom_section' : 'image';
      }

      if (layoutSection) {
        // Layout view: the pin belongs to a slide/page, not a bare image —
        // the pptx amender consumes exactly this sectionId shape.
        body.sectionId = layoutSection.sectionId;
        body.sectionType = layoutSection.sectionType;
        if (layoutThumb) body.anchorThumbnail = layoutThumb;
      }

      if (videoTimestamp !== null) {
        body.timestamp = videoTimestamp;
        body.sectionType = 'timestamp';
        body.sectionId = `time-${videoTimestamp}`;
      }

      // Tag webpage comments with the current state so they only render on the right screenshot
      if (isWebpage && !isViewingChild && hasMultipleStates) {
        const stateMeta = webpageStates.find(s => s.stateId === activeStateId);
        body.stateId = activeStateId;
        body.stateLabel = stateMeta?.label || '';
      }

      const targetCycleId = isViewingChild ? activeTab : cycle._id;
      await axios.post(
        `${backendUrl}/api/content-review/cycles/${targetCycleId}/comments`,
        body, { headers }
      );

      // Close the comment form — video stays at current position. The
      // composer's own fields reset by unmounting when the anchor clears.
      setSelectedSection(null);
      setTextSelection(null);
      setClickCoords(null);
      setVideoTimestamp(null);
      setLayoutSection(null);
      setLayoutPage(null);
      setLayoutThumb(null);
      setManualComment(false);
      window.getSelection()?.removeAllRanges();

      // Refresh comments locally without re-rendering parent
      try {
        const refreshHeaders = await getAuthHeaders();
        const targetCycleId2 = isViewingChild ? activeTab : cycle._id;
        const refreshRes = await axios.get(`${backendUrl}/api/content-review/cycles/${targetCycleId2}`, { headers: refreshHeaders });
        if (isViewingChild) {
          setChildData(prev => ({
            ...prev,
            [activeTab]: { ...prev[activeTab], comments: refreshRes.data.comments }
          }));
        } else {
          setLocalComments(refreshRes.data.comments);
        }
      } catch (refreshErr) {
        console.error('Error refreshing comments:', refreshErr);
      }

      // Notify parent to refresh its data (Comment Summary, etc.)
      onCommentAdded();

      // Restore scroll position after React has flushed the re-render
      // (form close, parent refetch, layout shift). Try multiple frames to
      // catch the layout after the new screenshot image has loaded too.
      restoreScroll();
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 200);
    } catch (err: any) {
      console.error('Error submitting comment:', err);
      setSubmitError(
        err?.response?.data?.error ||
        'Could not save that comment. Check the message below and try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const cancelComment = () => {
    setSelectedSection(null);
    setTextSelection(null);
    setClickCoords(null);
    setVideoTimestamp(null);
    setManualComment(false);
    window.getSelection()?.removeAllRanges();
  };

  // Get comments for a specific section (for inline display — filtered by role)
  const getCommentsForSection = (sectionId: string) =>
    visibleInlineComments.filter(c => c.sectionId === sectionId);

  if (loading) {
    return <div style={{ padding: 20, color: '#666' }}>Loading document content...</div>;
  }

  const handleTriggerParse = async () => {
    try {
      setParsing(true);
      const headers = await getAuthHeaders();
      const res = await axios.post(`${backendUrl}/api/content-review/cycles/${cycle._id}/parse`, {}, { headers });
      setSections(res.data.sections || []);
      setChildren(res.data.children || res.data.embeddedMedia?.map(() => ({})) || []);
    } catch (err) {
      console.error('Parse failed:', err);
    } finally {
      setParsing(false);
    }
  };

  // Parent + child tab bar, shared by BOTH render paths.
  //
  // 2026-08-19: this used to exist twice — the snapshot short-circuit below
  // carried its own older copy, so web-page cycles (every new one, since
  // ENABLE_SNAPSHOT_MODE is on in prod) showed a tab strip with none of the
  // things added the same morning: the rule stated above the tabs, the
  // "Main content" / "Embedded N of M" labels, the per-tab reviewed marker,
  // and the child delete control. Fredrik hit it immediately — as DM he
  // could see the tabs but no explanation of them. One definition now, so
  // the next tab-strip change cannot land on only one of the two paths.
  // "N comments mention you" strip (2026-08-20 rev 2).
  //
  // The overlay marker assumes the reviewer happens to LOOK at the right part
  // of a long page, and the comment card assumes they scroll past the viewer,
  // the assessment panel and the consultations panel to reach it. Both
  // assumptions failed in the prod run. This asks for neither: it sits at the
  // top of the content, states the count, and jumps straight to the comment.
  //
  // Counts across every tab, not just the active one — a mention on the
  // embedded video is exactly the one a reviewer would otherwise never see,
  // and panels below the viewer are cycle-wide for the same reason.
  const mentionsForMe = (() => {
    const seen = new Set<string>();
    const out: ReviewComment[] = [];
    const pools: ReviewComment[][] = [localComments];
    for (const key of Object.keys(childData || {})) {
      const kid = (childData as any)[key];
      if (kid?.comments) pools.push(kid.comments);
    }
    for (const pool of pools) {
      for (const c of pool) {
        if (c.resolved || seen.has(c._id)) continue;
        if (!mentionsViewer(c, user?.email)) continue;
        seen.add(c._id);
        out.push(c);
      }
    }
    return out;
  })();

  const mentionStrip = mentionsForMe.length > 0 && (
    <div className="viewer-mention-strip" data-testid="mention-strip">
      <span style={{ fontSize: 16 }}>&#64;</span>
      <span style={{ flex: 1 }}>
        <strong>
          {mentionsForMe.length === 1
            ? 'A comment mentions you'
            : `${mentionsForMe.length} comments mention you`}
        </strong>{' '}
        — a reply is expected.
      </span>
      <button onClick={() => openComment(mentionsForMe[0]._id)}>
        {mentionsForMe.length === 1 ? 'Go to it' : 'Go to the first'}
      </button>
    </div>
  );

  const tabBar = children.length > 0 && (
        <>
        {/* 2026-08-24 — this told EVERY viewer to mark tabs reviewed "before you
            can submit your assessment". For a producer both halves were false:
            they submit no assessment, and until today they could not mark
            anything. Same shape as the button they could not reach — the page
            describing a path the reader cannot take. */}
        <div className="viewer-tab-hint">
          This review has <strong>{children.length} embedded item{children.length === 1 ? '' : 's'}</strong> alongside
          the main content. {composerMode === 'reply-only'
            ? 'The findings on each item live on its own tab — read them there and confirm, on every tab, before you can mark the review ready to distribute.'
            : 'Every tab must be marked reviewed before you can submit your assessment.'}
        </div>
        <div className="viewer-state-switcher">
          <button
            className={`viewer-state-btn ${activeTab === 'parent' ? 'active' : ''}`}
            onClick={() => setActiveTab('parent')}
          >
            <span className="viewer-tab-kind">Main content</span>
            {CONTENT_TYPE_ICONS[cycle.contentType || ''] || 'Content'}: {cycle.contentItem.name.substring(0, 30)}{cycle.contentItem.name.length > 30 ? '...' : ''}
          </button>
          {children.map((c, idx) => (
            <span key={c._id} className="viewer-child-tab">
              <button
                className={`viewer-state-btn ${activeTab === c._id ? 'active' : ''}`}
                onClick={() => setActiveTab(c._id)}
              >
                <span className="viewer-tab-kind">
                  Embedded {idx + 1} of {children.length}
                  {/* A decided child shows its decision — "not reviewed" is a
                      per-viewer prompt that makes no sense once the cycle is
                      closed (Fredrik, migrated row:3 walk 2026-09-04). */}
                  {(c.status === 'approved' || c.status === 'rejected')
                    ? <span className="viewer-tab-reviewed" title={`Decision: ${c.status}`}> &#10003; {c.status}</span>
                    : (c.reviewedBy || []).some(r => r?.reviewer?.email?.toLowerCase() === user?.email?.toLowerCase())
                    ? <span className="viewer-tab-reviewed" title="You have marked this reviewed"> &#10003; reviewed</span>
                    : <span className="viewer-tab-pending" title="Not yet marked reviewed"> &#9679; not reviewed</span>}
                </span>
                {CONTENT_TYPE_ICONS[c.contentType || ''] || 'Item'}: {c.contentItem.name.substring(0, 25)}{c.contentItem.name.length > 25 ? '...' : ''}
              </button>
              {canDeleteChild && (
                <button
                  className="viewer-child-delete"
                  title={`Delete "${c.contentItem.name}"`}
                  aria-label={`Delete ${c.contentItem.name}`}
                  disabled={deletingChild}
                  onClick={() => handleDeleteChild(c._id, c.contentItem.name)}
                >
                  &times;
                </button>
              )}
            </span>
          ))}
        </div>
        </>
      );

  // Phase 11 — snapshot mode short-circuit. When the cycle was captured as
  // a self-contained HTML snapshot (single-file-cli) the entire multi-state
  // screenshot pipeline is bypassed; SnapshotViewer renders the snapshot in
  // a sandboxed iframe with comment-mode rectangle drag (Phase 11.2).
  // When viewing a CHILD (e.g. an embedded video) we fall through to the
  // normal render path which already handles video / audio / image cycles.
  if (cycle.contentType === 'webpage' && cycle.contentSubtype === 'snapshot' && !isViewingChild) {
    return (
      <div className="document-viewer">
        {cycle.sourceUrl && (
          <div style={{ marginBottom: 12 }}>
            <a href={cycle.sourceUrl} target="_blank" rel="noopener noreferrer"
               style={{ fontSize: 13, color: '#123250', fontWeight: 500 }}>
              Open original page &rarr;
            </a>
          </div>
        )}

        {mentionStrip}
        {tabBar}

        <SnapshotViewer
          user={user}
          cycle={cycle}
          comments={localComments}
          getAuthHeaders={getAuthHeaders}
          onCommentAdded={onCommentAdded}
          readOnly={readOnly}
          composerMode={composerMode}
          commentVisibility={commentVisibility}
          refreshKey={refreshKey}
          canEditAnyComment={canEditAnyComment}
          renderCommentDetail={renderCommentDetail}
          commentsHeader={commentsHeader}
        />
      </div>
    );
  }

  if (sections.length === 0 && !screenshotUrl && !isViewingChild && cycle.contentType !== 'video' && cycle.contentType !== 'image') {
    const PARSEABLE_FILE_TYPES = ['docx', 'pptx', 'pdf'];
    const fileParsing = PARSEABLE_FILE_TYPES.includes(cycle.contentType || '') && cycle.contentItem?.fileId;
    return (
      <div style={{ padding: 20, color: '#888' }}>
        {cycle.contentType === 'webpage' ? (
          <p style={{ fontStyle: 'italic', marginBottom: 12 }}>
            Scraping web page content... This may take a few seconds.
          </p>
        ) : fileParsing ? (
          <p style={{ fontStyle: 'italic', marginBottom: 12 }}>
            Parsing document... This may take a few seconds for large files.
          </p>
        ) : (
          <p style={{ fontStyle: 'italic', marginBottom: 12 }}>Content not yet parsed.</p>
        )}
        {cycle.contentItem.sharePointPath && (
          <div style={{ marginBottom: 12 }}>
            <a href={cycle.contentItem.sharePointPath} target="_blank" rel="noopener noreferrer"
               style={{ fontSize: 13, color: '#123250' }}>
              Open original file in SharePoint &rarr;
            </a>
          </div>
        )}
        {cycle.sourceUrl && (
          <div style={{ marginBottom: 12 }}>
            <a href={cycle.sourceUrl} target="_blank" rel="noopener noreferrer"
               style={{ fontSize: 13, color: '#123250' }}>
              Open original page &rarr;
            </a>
          </div>
        )}
        {!readOnly && cycle.contentItem.fileId && (
          <button
            className="review-btn review-btn-primary"
            onClick={handleTriggerParse}
            disabled={parsing}
            style={{ fontSize: 13 }}
          >
            {parsing ? 'Parsing...' : 'Parse Content from SharePoint'}
          </button>
        )}
      </div>
    );
  }

  // Render section text with highlighted ranges from existing comments
  /**
   * Ids this SECTION is the anchor for.
   *
   * A comment made without selecting text (the composer's manual path, and
   * every pre-selection comment in the seeded data) has no <mark> to scroll
   * to — the rail would offer no jump at all for it, which is most of the
   * comments on some cycles. The section it belongs to is the honest anchor:
   * less precise than a highlight, and far better than nothing.
   *
   * Text-selected comments are deliberately excluded — their <mark> is the
   * better target, and listing them here too would make the whole section
   * compete with the highlight inside it for the same id.
   */
  const sectionAnchorIds = (sectionComments: ReviewComment[]): string | undefined => {
    const ids = sectionComments.filter(c => !c.textSelection).map(c => c._id);
    return ids.length ? ids.join(' ') : undefined;
  };

  const renderTextWithHighlights = (section: ParsedSection, sectionComments: ReviewComment[]) => {
    const text = section.content || '';
    if (sectionComments.length === 0) return text;

    // Normalize comment ranges — invalid ranges (start=end=0) become full section
    const withRange = sectionComments
      .filter(c => c.textSelection)
      .map(c => {
        let start = c.selectionStart ?? 0;
        let end = c.selectionEnd ?? 0;
        // If range is invalid, highlight the whole section
        if (end <= start) { start = 0; end = text.length; }
        // Clamp to text bounds
        start = Math.max(0, Math.min(start, text.length));
        end = Math.max(start, Math.min(end, text.length));
        return { start, end, severity: c.severity, id: c._id, comment: c };
      })
      .filter(h => h.end > h.start)
      .sort((a, b) => a.start - b.start);

    if (withRange.length === 0) return text;

    const parts: React.ReactNode[] = [];
    let lastEnd = 0;

    for (const h of withRange) {
      if (h.start < lastEnd) continue;
      if (h.start > lastEnd) {
        parts.push(text.substring(lastEnd, h.start));
      }

      // Find all comments whose ranges overlap with this highlight
      const overlapping = sectionComments.filter(c => {
        if (!c.textSelection) return false;
        let cs = c.selectionStart ?? 0;
        let ce = c.selectionEnd ?? 0;
        if (ce <= cs) { cs = 0; ce = text.length; } // same normalization
        return cs < h.end && ce > h.start; // range overlap
      });

      const handleHighlightHover = (e: React.MouseEvent) => {
        const containerRect = contentRef.current?.getBoundingClientRect();
        if (containerRect) {
          setHoveredComments({
            comments: overlapping,
            x: e.clientX - containerRect.left,
            y: e.clientY - containerRect.top
          });
        }
      };

      // Clicking the highlight jumps to the comment card (2026-08-20,
      // Fredrik). Hover already previewed it, but the only way to actually
      // REACH a comment was the Consultations panel — which lists mentions
      // only, so an ordinary comment on the text you are reading had no path
      // from the document at all. Where a highlight covers several overlapping
      // comments, the first is the scroll target; the popover lists them all
      // and each row is independently clickable.
      const primaryId = overlapping[0]?._id || h.id;
      // Same mention axis as the screenshot overlays and the video timeline:
      // severity owns the hue, the violet outline + @ marker mean "addressed
      // to you". One visual language across every content type, so a reviewer
      // learns it once (2026-08-20 rev 2).
      const { forMe: markForMe } = markerMentionProps(overlapping, user?.email);
      parts.push(
        <mark
          key={h.id}
          // Anchor for the rail's jump + scroll spy. The cluster list matters:
          // one <mark> can cover several overlapping comments, and each of
          // them has to resolve to this element (commentAnchors.tsx).
          data-comment-id={primaryId}
          data-comment-ids={overlapping.map(o => o._id).join(' ') || h.id}
          className={`viewer-highlight ${h.severity}${markForMe ? ' mentions-you' : ''}`}
          onMouseEnter={handleHighlightHover}
          onMouseLeave={() => setHoveredComments(null)}
          onClick={() => openComment(primaryId)}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openComment(primaryId); } }}
          style={{ cursor: 'pointer' }}
          title={markForMe ? 'You were mentioned here — click to open and reply' : 'Open this comment'}
        >
          {text.substring(h.start, h.end)}
          {markForMe && <span className="viewer-mention-inline" title="You were mentioned here">@</span>}
        </mark>
      );
      lastEnd = h.end;
    }
    if (lastEnd < text.length) {
      parts.push(text.substring(lastEnd));
    }
    return <>{parts}</>;
  };

  // Show comment form: for text content only when text is selected; for screenshots/images on click/drag; for video on timestamp
  const isTextContent = ['docx', 'pptx', 'pdf'].includes(activeCycle?.contentType || cycle.contentType || '');
  const showCommentForm = (isTextContent ? !!textSelection : !!selectedSection) || clickCoords || videoTimestamp !== null || manualComment;

  return (
    <div className="document-viewer">
      {/* Original content link — shown consistently for files (SharePoint)
          AND web pages (source URL), for parent or active child */}
      {(() => {
        const activeItem = isViewingChild ? activeChild?.cycle : cycle;
        const spPath = activeItem?.contentItem.sharePointPath;
        const srcUrl = activeItem?.sourceUrl;
        if (spPath) {
          return (
            <div style={{ marginBottom: 12 }}>
              <a href={spPath} target="_blank" rel="noopener noreferrer"
                 style={{ fontSize: 13, color: '#123250', fontWeight: 500 }}>
                Open original file in SharePoint &rarr;
              </a>
            </div>
          );
        }
        if (srcUrl) {
          return (
            <div style={{ marginBottom: 12 }}>
              <a href={srcUrl} target="_blank" rel="noopener noreferrer"
                 style={{ fontSize: 13, color: '#123250', fontWeight: 500 }}>
                Open original page &rarr;
              </a>
            </div>
          );
        }
        return null;
      })()}

      {/* Parent + child tab bar.
          2026-08-19: reviewers could not tell the main content from its embedded
          items, nor that every tab has to be reviewed before an assessment can
          be submitted — the rule was enforced but never stated, so the first a
          reviewer heard of it was an error message on submit. The tabs now say
          which is which, carry a per-tab reviewed marker, and the rule is
          written above them. */}
      {mentionStrip}
      {tabBar}

      {/* Loading child */}
      {loadingChild && (
        <div style={{ padding: 20, color: '#666' }}>Loading content...</div>
      )}

      <CommentAnchorProvider value={anchorsWithViewSwitch}>
      <div className="viewer-layout">
        {/* Content area */}
        <div className="viewer-content" ref={attachContentRef} onMouseUp={handleContentMouseUp}>
          {/* Reviewer guidance */}
          {layoutCapable && (
            <div data-testid="view-mode-toggle" style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
              {(['copy', 'layout'] as const).map(m => (
                <button
                  key={m}
                  className={`review-btn ${viewMode === m ? '' : 'review-btn-outline'}`}
                  style={{ fontSize: 12, padding: '5px 14px' }}
                  onClick={() => { setViewMode(m); setClickCoords(null); setSelectedSection(null); setTextSelection(null); }}
                  title={m === 'copy'
                    ? 'The extracted text — select passages for precise, auto-applicable findings'
                    : 'The designed pages — click to pin comments on anything, including images'}
                >
                  {m === 'copy' ? 'Text' : 'Layout'}
                </button>
              ))}
            </div>
          )}
          {layoutCapable && canAuthor && (
            <div className="viewer-guidance" data-testid="view-mode-hint">
              {viewMode === 'copy'
                ? <>You're reading the extracted text. <strong>Select a passage</strong> to comment on specific wording — these comments can be applied to the file automatically. For images, diagrams or anything visual, switch to <strong>Layout</strong>.</>
                : <>You're viewing the designed pages. <strong>Click</strong> to pin a comment, or <strong>drag a box</strong> around an image or area — a snapshot of what you marked is saved with the comment. To comment on the wording itself, switch to <strong>Text</strong>.</>}
            </div>
          )}

          {layoutCapable && viewMode === 'layout' && (
            <div data-testid="layout-view" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {(!layoutState || layoutState.status === 'generating') && (
                <p style={{ fontSize: 13, color: '#666' }}>Rendering pages… this takes a few seconds the first time.</p>
              )}
              {layoutState?.status === 'failed' && (
                <p style={{ fontSize: 13, color: '#dc3545' }}>Could not render the layout: {layoutState.error}</p>
              )}
              {layoutState?.status === 'ready' && Array.from({ length: layoutState.pages || 0 }, (_, i) => {
                const secId = layoutSectionIdFor(i);
                const pins = visibleInlineComments.filter(c => c.coordinates && c.sectionId === secId);
                const size = layoutImgSizes[i];
                const label = cycle.contentType === 'pptx' ? `Slide ${i + 1}` : `Page ${i + 1}`;
                return (
                  <div key={i} className="viewer-layout-page" style={{ maxWidth: 960 }}>
                    <div className="viewer-slide-header">
                      <span>{label}</span>
                      {pins.length > 0 && <span style={{ fontSize: 12, color: '#666', fontWeight: 400 }}>{pins.length} comment{pins.length === 1 ? '' : 's'}</span>}
                    </div>
                    {layoutUrls[i] ? (
                      <div
                        style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}
                        onMouseMove={handleLayoutMouseMove}
                        onMouseUp={() => handleLayoutMouseUp(i)}
                        onMouseLeave={() => handleLayoutMouseUp(i)}
                      >
                        <img
                          src={layoutUrls[i]}
                          alt={label}
                          draggable={false}
                          style={{ width: '100%', display: 'block', border: '1px solid #e0e0e0', borderRadius: 6, cursor: canAuthor ? 'crosshair' : 'default' }}
                          onLoad={e => { const img = e.currentTarget; setLayoutImgSizes(prev => ({ ...prev, [i]: { w: img.naturalWidth, h: img.naturalHeight } })); }}
                          onMouseDown={e => handleLayoutMouseDown(i, e)}
                          title={canAuthor ? 'Click to pin a comment, or drag to mark an area' : undefined}
                        />
                        {size && pins.map(c => {
                          const { forMe } = markerMentionProps([c], user?.email);
                          const hasRect = c.coordinates!.width && c.coordinates!.height;
                          return hasRect ? (
                            <div
                              key={c._id}
                              data-comment-id={c._id}
                              className={`viewer-rect ${c.severity}${forMe ? ' mentions-you' : ''}`}
                              style={{
                                left: `${(c.coordinates!.x / size.w) * 100}%`,
                                top: `${(c.coordinates!.y / size.h) * 100}%`,
                                width: `${(c.coordinates!.width! / size.w) * 100}%`,
                                height: `${(c.coordinates!.height! / size.h) * 100}%`,
                                pointerEvents: 'auto', cursor: 'pointer',
                                ...(forMe ? { borderColor: '#7c3aed', borderWidth: 3, background: 'rgba(124,58,237,0.14)' } : {})
                              }}
                              title={forMe ? 'You were mentioned here — click to open and reply' : c.issue}
                              onClick={ev => { ev.stopPropagation(); openComment(c._id); }}
                            >
                              {forMe && <span className="viewer-mention-flag" title="You were mentioned here">@ YOU</span>}
                            </div>
                          ) : (
                            <div
                              key={c._id}
                              data-comment-id={c._id}
                              className={`viewer-pin ${c.severity}${forMe ? ' mentions-you' : ''}`}
                              style={{
                                left: `${(c.coordinates!.x / size.w) * 100}%`,
                                top: `${(c.coordinates!.y / size.h) * 100}%`,
                                pointerEvents: 'auto', cursor: 'pointer',
                                ...(forMe ? { background: '#7c3aed', borderColor: '#5b21b6' } : {})
                              }}
                              title={forMe ? 'You were mentioned here — click to open and reply' : c.issue}
                              onClick={ev => { ev.stopPropagation(); openComment(c._id); }}
                            >
                              {forMe && <span className="viewer-mention-flag" title="You were mentioned here">@ YOU</span>}
                            </div>
                          );
                        })}
                        {isDragging && dragRect && layoutPage === i && size && (
                          <div
                            className="viewer-rect active"
                            style={{
                              left: `${(dragRect.x / size.w) * 100}%`,
                              top: `${(dragRect.y / size.h) * 100}%`,
                              width: `${(dragRect.width / size.w) * 100}%`,
                              height: `${(dragRect.height / size.h) * 100}%`
                            }}
                          />
                        )}
                        {clickCoords && !clickCoords.width && layoutPage === i && size && (
                          <div
                            className="viewer-pin active"
                            style={{ left: `${(clickCoords.x / size.w) * 100}%`, top: `${(clickCoords.y / size.h) * 100}%` }}
                          />
                        )}
                        {clickCoords?.width && clickCoords.height && layoutPage === i && size && (
                          <div
                            className="viewer-rect active"
                            style={{
                              left: `${(clickCoords.x / size.w) * 100}%`,
                              top: `${(clickCoords.y / size.h) * 100}%`,
                              width: `${(clickCoords.width / size.w) * 100}%`,
                              height: `${(clickCoords.height / size.h) * 100}%`
                            }}
                          />
                        )}
                      </div>
                    ) : (
                      <p style={{ fontSize: 12, color: '#999' }}>Loading page…</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* The per-view hint under the Copy/Layout toggle replaced the old
              generic "select text to comment" guidance line (2026-09-01). */}
          {/* Decision maker: toggle inline comments */}
          {readOnly && visibleInlineComments.length > 0 && (
            <div className="viewer-guidance" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{allActiveComments.length} comment{allActiveComments.length !== 1 ? 's' : ''} from reviewers</span>
              <button
                className="review-btn review-btn-outline"
                onClick={() => setShowInlineComments(!showInlineComments)}
                style={{ fontSize: 12, padding: '4px 12px' }}
              >
                {showInlineComments ? 'Hide inline comments' : 'Show inline comments'}
              </button>
            </div>
          )}
          {readOnly && !showInlineComments && allActiveComments.length > 0 && (
            <div className="viewer-guidance" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Inline comments hidden ({allActiveComments.length} total)</span>
              <button
                className="review-btn review-btn-outline"
                onClick={() => setShowInlineComments(true)}
                style={{ fontSize: 12, padding: '4px 12px' }}
              >
                Show inline comments
              </button>
            </div>
          )}

          {/* Hover popover for text highlights (must be inside viewer-content for positioning) */}
          {hoveredComments && isTextContent && (
            <div className="viewer-comment-popover" style={{ left: hoveredComments.x, top: hoveredComments.y }}>
              {hoveredComments.comments.map(c => (
                <div key={c._id} className="viewer-comment-popover-item">
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 12 }}>{c.reviewer.name}</span>
                    <span className={`severity-badge ${c.severity}`} style={{ fontSize: 10, padding: '0 6px' }}>{c.severity}</span>
                  </div>
                  {c.textSelection && (
                    <div style={{ fontSize: 11, color: '#777', fontStyle: 'italic', marginBottom: 2, maxHeight: 30, overflow: 'hidden' }}>
                      "{c.textSelection.substring(0, 80)}{c.textSelection.length > 80 ? '...' : ''}"
                    </div>
                  )}
                  <p style={{ margin: 0, fontSize: 12 }}>{c.issue}</p>
                  {c.suggestedFix && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#155724' }}>Fix: {c.suggestedFix}</p>}
                </div>
              ))}
            </div>
          )}

          {/* DOCX: rendered text sections with text selection */}
          {viewMode === 'copy' && (activeCycle?.contentType || cycle.contentType) === 'docx' && activeSections.map(section => {
            const sectionComments = getCommentsForSection(section.sectionId);
            const hasComments = sectionComments.length > 0;
            return (
              <div
                key={section.sectionId}
                data-section-id={section.sectionId}
                data-comment-ids={sectionAnchorIds(sectionComments)}
                className={`viewer-section ${hasComments ? 'has-comments' : ''} ${selectedSection?.sectionId === section.sectionId ? 'selected' : ''}`}
              >
                {section.type === 'heading' ? (
                  <h3 className="viewer-heading">{renderTextWithHighlights(section, sectionComments)}</h3>
                ) : section.type === 'list' ? (
                  <div className="viewer-list">{renderTextWithHighlights(section, sectionComments)}</div>
                ) : (
                  <p className="viewer-paragraph">{renderTextWithHighlights(section, sectionComments)}</p>
                )}
              </div>
            );
          })}

          {/* PPTX: slides with text selection */}
          {viewMode === 'copy' && (activeCycle?.contentType || cycle.contentType) === 'pptx' && (
            <div className="viewer-slides">
              {activeSections.map(section => {
                const sectionComments = getCommentsForSection(section.sectionId);
                const hasComments = sectionComments.length > 0;
                return (
                  <div
                    key={section.sectionId}
                    data-section-id={section.sectionId}
                    data-comment-ids={sectionAnchorIds(sectionComments)}
                    className={`viewer-slide ${hasComments ? 'has-comments' : ''} ${selectedSection?.sectionId === section.sectionId ? 'selected' : ''}`}
                  >
                    <div className="viewer-slide-header">
                      <span>Slide {section.metadata?.slideNumber}</span>
                      {hasComments && (
                        <span
                          className="viewer-comment-badge"
                          role="button"
                          tabIndex={0}
                          style={{ cursor: 'pointer' }}
                          title="Open the first comment on this slide"
                          onClick={() => openComment(sectionComments[0]._id)}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openComment(sectionComments[0]._id); } }}
                        >
                          {sectionComments.length} comment{sectionComments.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div className="viewer-slide-content" data-section-id={section.sectionId}>
                      {renderTextWithHighlights(section, sectionComments)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* PDF: page sections with text selection */}
          {viewMode === 'copy' && (activeCycle?.contentType || cycle.contentType) === 'pdf' && activeSections.map(section => {
            const sectionComments = getCommentsForSection(section.sectionId);
            const hasComments = sectionComments.length > 0;
            return (
              <div
                key={section.sectionId}
                data-section-id={section.sectionId}
                data-comment-ids={sectionAnchorIds(sectionComments)}
                className={`viewer-page ${hasComments ? 'has-comments' : ''} ${selectedSection?.sectionId === section.sectionId ? 'selected' : ''}`}
              >
                <div className="viewer-page-header">
                  <span>Page {section.metadata?.pageNumber} of {section.metadata?.totalPages}</span>
                  {hasComments && (
                    <span
                      className="viewer-comment-badge"
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                      title="Open the first comment on this page"
                      onClick={() => openComment(sectionComments[0]._id)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openComment(sectionComments[0]._id); } }}
                    >
                      {sectionComments.length} comment{sectionComments.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <pre className="viewer-page-content" data-section-id={section.sectionId}>{renderTextWithHighlights(section, sectionComments)}</pre>
              </div>
            );
          })}

          {/* Web page: screenshot with rectangle/pin commenting */}
          {(activeCycle?.contentType || cycle.contentType) === 'webpage' && screenshotUrl && (
            <div className="viewer-screenshot-container">
              {/* State switcher — shown when page has tabs or carousels */}
              {hasMultipleStates && (
                <div className="viewer-state-switcher">
                  {webpageStates.map(state => {
                    // Count comments on this state
                    const stateComments = allActiveCommentsRaw.filter(
                      c => (c.stateId || 'default') === state.stateId
                    );
                    return (
                      <button
                        key={state.stateId}
                        className={`viewer-state-btn ${activeStateId === state.stateId ? 'active' : ''}`}
                        onClick={() => {
                          setActiveStateId(state.stateId);
                          setImageLoaded(false);
                          setSelectedSection(null);
                          setClickCoords(null);
                          setTextSelection(null);
                        }}
                      >
                        {state.label}
                        {stateComments.length > 0 && (
                          <span className="viewer-state-count">{stateComments.length}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {canAuthor && (
                <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
                  Drag to select an area, or click to pin a comment
                </p>
              )}
              <div
                style={{ position: 'relative', display: 'inline-block', userSelect: 'none' }}
                onMouseMove={handleScreenshotMouseMove}
                onMouseUp={handleScreenshotMouseUp}
                onMouseLeave={handleScreenshotMouseUp}
              >
                <img
                  ref={imageRef}
                  src={screenshotUrl}
                  alt="Web page screenshot"
                  className="viewer-screenshot"
                  onMouseDown={handleScreenshotMouseDown}
                  onLoad={() => setImageLoaded(true)}
                  draggable={false}
                />
                {/* Render saved comment rectangles/pins (only after image loads so dimensions are correct) */}
                {imageLoaded && visibleInlineComments.filter(c => c.coordinates).map(c => {
                  const natW = imageRef.current?.naturalWidth || 1;
                  const natH = imageRef.current?.naturalHeight || 1;
                  const hasRect = c.coordinates!.width && c.coordinates!.height;
                  const cx = c.coordinates!.x, cy = c.coordinates!.y;
                  const cw = c.coordinates!.width || 0, ch = c.coordinates!.height || 0;

                  // Find overlapping comments (rectangles that overlap, or pins within 20px)
                  const overlapping = visibleInlineComments.filter(other => {
                    if (!other.coordinates) return false;
                    const ox = other.coordinates.x, oy = other.coordinates.y;
                    const ow = other.coordinates.width || 0, oh = other.coordinates.height || 0;
                    if (hasRect && ow && oh) {
                      // Rectangle overlap check
                      return cx < ox + ow && cx + cw > ox && cy < oy + oh && cy + ch > oy;
                    }
                    // Pin proximity
                    return Math.abs(ox - cx) < 20 && Math.abs(oy - cy) < 20;
                  });
                  const isFirst = overlapping[0]?._id === c._id;
                  const count = overlapping.length;

                  const handleHover = (e: React.MouseEvent) => {
                    const containerRect = e.currentTarget.closest('.viewer-screenshot-container')?.getBoundingClientRect();
                    if (containerRect) {
                      setHoveredComments({
                        comments: overlapping,
                        x: e.clientX - containerRect.left,
                        y: e.clientY - containerRect.top
                      });
                    }
                  };

                  // A comment addressed to you is emphasised ON THE PAGE, not
                  // only in the summary far below it (2026-08-20, Fredrik).
                  // The summary treatment shipped first and was invisible in
                  // practice: a reviewer reads the document, and a question
                  // waiting for them sat several screens down looking like
                  // every other finding.
                  const { forMe } = markerMentionProps(overlapping, user?.email);
                  return hasRect ? (
                    <div
                      key={c._id}
                      data-comment-id={c._id}
                      data-comment-ids={overlapping.map(o => o._id).join(' ')}
                      className={`viewer-rect ${c.severity}${forMe ? ' mentions-you' : ''}`}
                      style={{
                        left: `${(c.coordinates!.x / natW) * 100}%`,
                        top: `${(c.coordinates!.y / natH) * 100}%`,
                        width: `${(c.coordinates!.width! / natW) * 100}%`,
                        height: `${(c.coordinates!.height! / natH) * 100}%`,
                        pointerEvents: 'auto', cursor: 'pointer',
                        ...(forMe ? { borderColor: '#7c3aed', borderWidth: 3, background: 'rgba(124,58,237,0.14)' } : {})
                      }}
                      title={forMe ? 'You were mentioned here — click to open and reply' : 'Open this comment'}
                      onMouseEnter={handleHover}
                      onMouseLeave={() => setHoveredComments(null)}
                      onClick={() => openComment(c._id)}
                    >
                      {isFirst && (
                        <span className="viewer-rect-count">{count}</span>
                      )}
                      {forMe && <span className="viewer-mention-flag" title="You were mentioned here — click to open and reply">@ YOU</span>}
                    </div>
                  ) : (
                    <div
                      key={c._id}
                      data-comment-id={c._id}
                      data-comment-ids={overlapping.map(o => o._id).join(' ')}
                      className={`viewer-pin ${c.severity}${forMe ? ' mentions-you' : ''}`}
                      style={{
                        left: `${(c.coordinates!.x / natW) * 100}%`,
                        top: `${(c.coordinates!.y / natH) * 100}%`,
                        pointerEvents: 'auto', cursor: 'pointer',
                        ...(forMe ? { background: '#7c3aed', borderColor: '#5b21b6', boxShadow: '0 0 0 4px rgba(124,58,237,0.35)' } : {})
                      }}
                      title={forMe ? 'You were mentioned here — click to open and reply' : 'Open this comment'}
                      onMouseEnter={handleHover}
                      onMouseLeave={() => setHoveredComments(null)}
                      onClick={() => openComment(c._id)}
                    >
                      {isFirst && count > 1 && (
                        <span className="viewer-pin-count">{count}</span>
                      )}
                      {forMe && <span className="viewer-mention-flag" title="You were mentioned here — click to open and reply">@ YOU</span>}
                    </div>
                  );
                })}

                {/* Hover popover for screenshot comments */}
                {hoveredComments && (
                  <div className="viewer-comment-popover" style={{ left: hoveredComments.x, top: hoveredComments.y }}>
                    {hoveredComments.comments.map(c => (
                      <div key={c._id} className="viewer-comment-popover-item">
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                          <span style={{ fontWeight: 600, fontSize: 12 }}>{c.reviewer.name}</span>
                          <span className={`severity-badge ${c.severity}`} style={{ fontSize: 10, padding: '0 6px' }}>{c.severity}</span>
                        </div>
                        {c.anchorThumbnail ? (
                          <img src={c.anchorThumbnail} alt="Marked region" style={{ maxWidth: '100%', maxHeight: 80, display: 'block', border: '1px solid #ddd', borderRadius: 3, marginBottom: 2 }} />
                        ) : c.sectionContent ? (
                          <div style={{ fontSize: 11, color: '#777', fontStyle: 'italic', marginBottom: 2, maxHeight: 30, overflow: 'hidden' }}>
                            {c.sectionContent.substring(0, 80)}{c.sectionContent.length > 80 ? '...' : ''}
                          </div>
                        ) : null}
                        <p style={{ margin: 0, fontSize: 12 }}>{c.issue}</p>
                        {c.suggestedFix && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#155724' }}>Fix: {c.suggestedFix}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {/* Active drag rectangle */}
                {isDragging && dragRect && imageRef.current && (
                  <div
                    className="viewer-rect active"
                    style={{
                      left: `${(dragRect.x / imageRef.current.naturalWidth) * 100}%`,
                      top: `${(dragRect.y / imageRef.current.naturalHeight) * 100}%`,
                      width: `${(dragRect.width / imageRef.current.naturalWidth) * 100}%`,
                      height: `${(dragRect.height / imageRef.current.naturalHeight) * 100}%`
                    }}
                  />
                )}
                {/* Active click pin (small drag = pin) */}
                {clickCoords && !clickCoords.width && imageRef.current && (
                  <div
                    className="viewer-pin active"
                    style={{
                      left: `${(clickCoords.x / imageRef.current.naturalWidth) * 100}%`,
                      top: `${(clickCoords.y / imageRef.current.naturalHeight) * 100}%`
                    }}
                  />
                )}
                {/* Active rectangle result (after drag release) */}
                {clickCoords?.width && clickCoords.height && imageRef.current && (
                  <div
                    className="viewer-rect active"
                    style={{
                      left: `${(clickCoords.x / imageRef.current.naturalWidth) * 100}%`,
                      top: `${(clickCoords.y / imageRef.current.naturalHeight) * 100}%`,
                      width: `${(clickCoords.width / imageRef.current.naturalWidth) * 100}%`,
                      height: `${(clickCoords.height / imageRef.current.naturalHeight) * 100}%`
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Video: player with timestamp commenting.
              A media URL is a real file and plays in <video>. A provider URL is
              an HTML player PAGE and must be framed — see isProviderPlayerUrl. */}
          {(activeCycle?.contentType || cycle.contentType) === 'video' && (activeMediaUrl || activeCycle?.sourceUrl || cycle.sourceUrl) && (() => {
            const videoSrc = (activeMediaUrl || activeCycle?.sourceUrl || cycle.sourceUrl) as string;
            const isFramed = !activeMediaUrl && isProviderPlayerUrl(videoSrc);
            return (
            <div className="viewer-video-container">
              {isFramed ? (
                <iframe
                  src={playerSrcForEmbedding(videoSrc)}
                  className="viewer-video"
                  title="Embedded video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  ref={attachVideo}
                  src={videoSrc}
                  controls
                  className="viewer-video"
                />
              )}
              {canAuthor && (isFramed ? (
                // Timestamp commenting needs videoRef.currentTime, and a
                // cross-origin iframe will not give us that. Offering the
                // button anyway would produce comments stamped 0:00 — worse
                // than not offering it, because they would look deliberate.
                <div style={{ marginTop: 8 }}>
                  {framedTime === null && (
                    <p className="viewer-video-note" style={{ margin: '0 0 8px' }}>
                      This video is on an external player. Press play and the
                      comment button below will start timestamping — until then
                      a comment is filed against the video as a whole.
                    </p>
                  )}
                  <button
                    className="review-btn review-btn-primary"
                    onClick={() => {
                      // Same shape as handleVideoComment, but the position
                      // comes from the player's broadcast rather than
                      // videoRef, which a cross-origin iframe never exposes.
                      setSelectedSection(null);
                      setTextSelection(null);
                      setClickCoords(null);
                      if (framedTime !== null) {
                        setVideoTimestamp(Math.round(framedTime));
                        setManualComment(false);
                      } else {
                        setVideoTimestamp(null);
                        setManualComment(true);
                      }
                    }}
                  >
                    {framedTime !== null
                      ? `Comment at ${formatTimestamp(Math.round(framedTime))}`
                      : 'Add comment'}
                  </button>
                </div>
              ) : (
                <button
                  className="review-btn review-btn-primary"
                  onClick={handleVideoComment}
                  style={{ marginTop: 8 }}
                  data-testid="video-comment-at-time"
                >
                  {/* Counts, like the embedded-player button beside it. The
                      click still reads videoRef.currentTime rather than this
                      state, so the stamp is the exact position at press time
                      even if a tick is in flight — the label follows the
                      player, it does not define the value. */}
                  {`Comment at ${formatTimestamp(nativeTime)}`}
                </button>
              ))}
              {/* Timeline markers */}
              {visibleInlineComments.filter(c => c.timestamp !== undefined).length > 0 && (
                <div
                  className="viewer-timeline-markers"
                  style={{ position: 'relative' }}
                  onMouseLeave={() => setHoveredComments(null)}
                >
                  {visibleInlineComments.filter(c => c.timestamp !== undefined).map(c => {
                    const atSameTime = visibleInlineComments.filter(other =>
                      other.timestamp !== undefined &&
                      Math.abs(other.timestamp - c.timestamp!) <= 2
                    );
                    return (
                      <span
                        key={c._id}
                        className={`viewer-timeline-marker ${c.severity}${
                          markerMentionProps(atSameTime, user?.email).forMe ? ' mentions-you' : ''
                        }`}
                        title={markerMentionProps(atSameTime, user?.email).forMe
                          ? 'You were mentioned in a comment here'
                          : undefined}
                        onClick={() => {
                          if (videoRef.current) videoRef.current.currentTime = c.timestamp!;
                        }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const containerRect = e.currentTarget.closest('.viewer-timeline-markers')?.getBoundingClientRect();
                          if (containerRect) {
                            setHoveredComments({
                              comments: atSameTime,
                              x: rect.left - containerRect.left + rect.width / 2,
                              y: -8
                            });
                          }
                        }}
                      >
                        {formatTimestamp(c.timestamp!)}
                      </span>
                    );
                  })}
                  {hoveredComments && (
                    <div
                      className="viewer-comment-popover"
                      style={{ left: hoveredComments.x, top: hoveredComments.y, transform: 'translate(-50%, -100%)' }}
                    >
                      {hoveredComments.comments.map(c => (
                        <div key={c._id} className="viewer-comment-popover-item">
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                            <span style={{ fontWeight: 600, fontSize: 12 }}>{c.reviewer.name}</span>
                            <span className={`severity-badge ${c.severity}`} style={{ fontSize: 10, padding: '0 6px' }}>{c.severity}</span>
                            <span style={{ fontSize: 11, color: '#666' }}>at {formatTimestamp(c.timestamp!)}</span>
                          </div>
                          <p style={{ margin: 0, fontSize: 12 }}>{c.issue}</p>
                          {c.suggestedFix && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#155724' }}>Fix: {c.suggestedFix}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })()}

          {/* Image: direct display with pin commenting */}
          {(activeCycle?.contentType || cycle.contentType) === 'image' && (activeMediaUrl || activeCycle?.sourceUrl || cycle.sourceUrl) && (
            <div className="viewer-screenshot-container">
              <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
                Click anywhere on the image to add a comment
              </p>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  ref={imageRef}
                  src={activeMediaUrl || activeCycle?.sourceUrl || cycle.sourceUrl || ''}
                  alt="Reviewable content"
                  className="viewer-screenshot"
                  onClick={handleImageClick}
                />
                {/* The oldest marker copy — it predates visibility filtering,
                    clustering, popovers and click-to-open. Brought up to the
                    §10 baseline (2026-08-20): same visibility filter as every
                    other renderer, the shared mention decision, and a click
                    that opens the card. */}
                {visibleInlineComments.filter(c => c.coordinates).map(c => {
                  const { forMe } = markerMentionProps([c], user?.email);
                  return (
                    <div
                      key={c._id}
                      data-comment-id={c._id}
                      className={`viewer-pin ${c.severity}${forMe ? ' mentions-you' : ''}`}
                      style={{
                        left: `${(c.coordinates!.x / (imageRef.current?.naturalWidth || 1)) * 100}%`,
                        top: `${(c.coordinates!.y / (imageRef.current?.naturalHeight || 1)) * 100}%`,
                        pointerEvents: 'auto', cursor: 'pointer',
                        ...(forMe ? { background: '#7c3aed', borderColor: '#5b21b6' } : {})
                      }}
                      title={forMe ? 'You were mentioned here — click to open and reply' : c.issue}
                      onClick={() => openComment(c._id)}
                    >
                      {forMe && <span className="viewer-mention-flag" title="You were mentioned here">@ YOU</span>}
                    </div>
                  );
                })}
                {clickCoords && imageRef.current && (
                  <div
                    className="viewer-pin active"
                    style={{
                      left: `${(clickCoords.x / imageRef.current.naturalWidth) * 100}%`,
                      top: `${(clickCoords.y / imageRef.current.naturalHeight) * 100}%`
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Video/image without media URL yet — show load button.
              Two source paths: SharePoint-stored (has fileId → use download-url)
              or extracted-from-parent embedded media (has localFilePath → use
              the embedded-media stream endpoint). The frontend hides the load
              affordance entirely for cycles with neither (legacy children that
              the parser never extracted). */}
          {((activeCycle?.contentType || cycle.contentType) === 'video' || (activeCycle?.contentType || cycle.contentType) === 'image') &&
           !activeMediaUrl && !(activeCycle?.sourceUrl || cycle.sourceUrl) && (() => {
            const c = activeCycle || cycle;
            const hasFileId = !!c.contentItem?.fileId;
            const hasLocalFile = !!c.contentItem?.localFilePath;
            if (!hasFileId && !hasLocalFile) return null;
            const isEmbedded = hasLocalFile && !hasFileId;
            return (
              <div style={{ padding: 20 }}>
                <p style={{ color: '#888', marginBottom: 12 }}>
                  {isEmbedded
                    ? `Embedded ${c.contentType === 'video' ? 'video' : 'image'} from the parent document — click to load.`
                    : `${c.contentType === 'video' ? 'Video' : 'Image'} is stored in SharePoint.`}
                </p>
                <button
                  className="review-btn review-btn-primary"
                  onClick={async () => {
                    const targetId = isViewingChild ? activeTab : cycle._id;
                    try {
                      const headers = await getAuthHeaders();
                      if (isEmbedded) {
                        // Fetch the binary directly as a blob — local files
                        // require our auth headers and can't be linked from a
                        // signed SharePoint URL.
                        const res = await axios.get(
                          `${backendUrl}/api/content-review/cycles/${targetId}/embedded-media`,
                          { headers, responseType: 'blob' }
                        );
                        const url = URL.createObjectURL(res.data);
                        if (isViewingChild) {
                          setChildData(prev => ({
                            ...prev,
                            [activeTab]: { ...prev[activeTab], mediaUrl: url }
                          }));
                        } else {
                          setMediaUrl(url);
                        }
                      } else {
                        const res = await axios.get(
                          `${backendUrl}/api/content-review/cycles/${targetId}/download-url`,
                          { headers }
                        );
                        if (res.data.downloadUrl) {
                          if (isViewingChild) {
                            setChildData(prev => ({
                              ...prev,
                              [activeTab]: { ...prev[activeTab], mediaUrl: res.data.downloadUrl }
                            }));
                          } else {
                            setMediaUrl(res.data.downloadUrl);
                          }
                        }
                      }
                    } catch (err: any) {
                      console.error('Failed to load media:', err);
                      alert(err.response?.data?.error || 'Failed to load media');
                    }
                  }}
                  style={{ fontSize: 13 }}
                >
                  {isEmbedded ? 'Load embedded media' : 'Load from SharePoint'}
                </button>
              </div>
            );
          })()}
        </div>

        {/* Comment form + consultation threads share the rail (2026-08-26).
            The composer used to BE the right column, so the rail vanished
            whenever nothing was selected — and threads lived in a section
            below the whole document, where you could not see the content you
            were answering about. ViewerSidePanel owns the column now and is
            rendered by SnapshotViewer too, so the web path cannot drift. */}
        <ViewerSidePanel
          cycleId={String(cycle._id)}
          getAuthHeaders={getAuthHeaders}
          refreshKey={refreshKey}
          viewerEmail={user?.email}
          canEditAny={canEditAnyComment}
          // The rail lists the ACTIVE tab's findings. Built from
          // applyVisibility rather than visibleInlineComments on purpose:
          // "Hide inline comments" hides the MARKERS so the DM can read the
          // document clean, and emptying the list with them would take the
          // comments away from the person who just asked to see the content
          // without them. Roots only — a reply is part of its thread.
          comments={railComments}
          renderCommentDetail={renderCommentDetail}
          commentsHeader={commentsHeader}
          banner={submitError ? (<div data-testid="comment-submit-error" style={{
            margin: '0 0 8px', padding: '8px 10px', borderRadius: 6,
            background: '#f8d7da', border: '1px solid #f5c2c7', color: '#842029', fontSize: 12
          }}>
            {submitError}
          </div>) : null}
          composer={showCommentForm && canAuthor ? (
          <CommentComposer
            title={videoTimestamp !== null
              ? `Comment at ${formatTimestamp(videoTimestamp)}`
              : layoutSection && layoutPage !== null && clickCoords
                ? `${cycle.contentType === 'pptx' ? 'Slide' : 'Page'} ${layoutPage + 1} — ${clickCoords.width && clickCoords.height ? 'marked area' : 'pinned location'}`
                : clickCoords?.width && clickCoords.height
                  ? 'Comment on selected area'
                  : clickCoords
                    ? 'Comment on pinned location'
                    : textSelection
                      ? 'Comment on selected text'
                      : 'Add comment'}
            quote={textSelection
              ? `"${textSelection.text}"`
              : selectedSection?.content || null}
            thumbnail={layoutThumb}
            quoteBorderColor={textSelection ? undefined : '#666'}
            // Phase 6 — "Replacement text" only where the system can apply
            // it: a text selection in a docx/pptx, or in a pdf whose
            // editable companion is a docx/pptx.
            canPropose={!!textSelection && hasEditableWorkingFile({
              contentType: activeCycle?.contentType || cycle.contentType,
              contentItem: (activeCycle?.contentItem?.sourceFileName
                ? activeCycle.contentItem
                : cycle.contentItem) as any
            })}
            allowFinding={composerMode !== 'consultation'}
            getAuthHeaders={getAuthHeaders}
            submitting={submitting}
            onSubmit={handleSubmitComment}
            onCancel={cancelComment}
          />
          ) : null}
        />
      </div>
      </CommentAnchorProvider>
    </div>
  );
};

function getSectionType(contentType: ContentType, section: ParsedSection): SectionType {
  if (contentType === 'docx') return 'text';
  if (contentType === 'pptx') return 'slide';
  if (contentType === 'pdf') return 'page';
  if (contentType === 'webpage') return 'dom_section';
  if (contentType === 'image') return 'image';
  if (contentType === 'video') return 'timestamp';
  return '';
}

function formatTimestamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default DocumentViewer;
